import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "prevButton", "nextButton", "finishButton", "recap", "form", "errors", "nameInput", "progress", "navigation", "kmInput", "poiInput", "waySelect"]
  static values = {
    totalSteps: Number,
    currentStep: { type: Number, default: 0 },
    showProgress: { type: Boolean, default: false },
    poiStartId: String,
    wayId: String,
    poiCache: { type: Object, default: {} }
  }
  static MINIMUM_SEARCH_LENGTH = 2
  static DEBOUNCE_DELAY = 50  // Réduction à 50ms pour plus de réactivité
  static POI_CACHE_SIZE = 50

  connect() {
    console.log("🧙‍♂️ Wizard controller connected")
    this.currentStepValue = 0
    this.clearStoredData()
    this.updateStepVisibility()

    if (this.hasPoiInputTarget) {
      this.poiInputTarget.disabled = true
      this.poiInputTarget.value = ""
      console.log("🧙‍♂️ Initialisation du champ POI")

      // Lier handleClickOutside au contexte actuel
      this.boundHandleClickOutside = this.handleClickOutside.bind(this)
      document.addEventListener('click', this.boundHandleClickOutside)

      // Créer une version debounced de searchPois
      this.debouncedSearchPois = this.debounce(this.searchPois.bind(this), this.constructor.DEBOUNCE_DELAY)
      this.poiInputTarget.addEventListener('input', this.debouncedSearchPois)
    }
  }

  disconnect() {
    if (this.hasPoiInputTarget) {
      this.poiInputTarget.removeEventListener('input', this.debouncedSearchPois)
      document.removeEventListener('click', this.boundHandleClickOutside)
    }

    this.clearStoredData()
  }

  async loadItineraries() {
    try {
      const response = await fetch('/api/way_itineraries')
      if (!response.ok) throw new Error('Erreur lors du chargement des itinéraires')
      
      const featureCollection = await response.json()
      const select = this.waySelectTarget
      
      // Vider le select et ajouter l'option par défaut
      select.options.length = 0
      select.add(new Option('Choisis une option...', ''))
      
      // Ajouter les options des itinéraires
      featureCollection.features.forEach(feature => {
        const p = feature.properties
        const option = new Option(p.name, p.way_id)
        option.dataset.cityStartId = p.city_start?.id || ''
        option.dataset.cityStartName = p.city_start?.name || ''
        select.add(option)
      })

      // Réinitialiser le champ POI
      if (this.hasPoiInputTarget) {
        const poiInput = this.poiInputTarget
        poiInput.disabled = true
        poiInput.value = ""
        poiInput.placeholder = "Sélectionne d'abord une voie..."
      }
    } catch (error) {
      console.error('❌ Erreur:', error)
      this.showError("Une erreur est survenue lors du chargement des itinéraires. Veuillez réessayer.")
    }
  }

  startWizard() {
    this.currentStepValue = 1
    this.showProgressValue = true
    this.progressTarget.classList.remove('hidden')
    this.navigationTarget.classList.remove('hidden')
    this.updateStepVisibility()
    this.updateButtonsVisibility()
    this.updateProgressBar()
  }

  // Méthode pour gérer le clic en dehors
  handleClickOutside = (e) => {
    const suggestionsList = document.querySelector('.poi-suggestions')
    if (!suggestionsList) return

    const isClickInside = e.target.closest('.poi-suggestions') || e.target.matches('[data-wizard-target="poiInput"]')
    if (!isClickInside) {
      this.clearSuggestions()
    }
  }

  async nextStep() {
    if (this.validateCurrentStep()) {
      if (this.currentStepValue < this.totalStepsValue - 1) {
        this.storeCurrentStepData()
        this.currentStepValue++
        
        // Attendre que l'étape soit mise à jour avant de mettre à jour les boutons
        await this.updateStepVisibility()
        
        // Mettre à jour les boutons et la barre de progression après le chargement de l'étape
        this.updateButtonsVisibility()
        this.updateProgressBar()
      }
    }
  }

  async previousStep() {
    if (this.currentStepValue > 1) {
      this.storeCurrentStepData()
      this.currentStepValue--
      
      // Attendre que l'étape soit mise à jour
      await this.updateStepVisibility()
      
      // Mettre à jour les boutons et la barre de progression après le chargement de l'étape
      this.updateButtonsVisibility()
      this.updateProgressBar()

      // Restaurer les données après que tout est chargé
      setTimeout(() => {
        this.restoreStepData()
        this.setupRequiredFields(this.stepTargets[this.currentStepValue])
      }, 100)
    }
  }

  storeCurrentStepData() {
    const currentStep = this.stepTargets[this.currentStepValue]
    const form = currentStep.querySelector('form')
    if (!form) return

    const formData = new FormData(form)
    const data = {}
    formData.forEach((value, key) => {
      const matches = key.match(/course\[(.*?)\]/)
      if (matches) {
        data[matches[1]] = value
      } else {
        data[key] = value
      }
    })

    sessionStorage.setItem(`wizard_step_${this.currentStepValue}`, JSON.stringify(data))
  }

  restoreStepData() {
    const storedData = sessionStorage.getItem(`wizard_step_${this.currentStepValue}`)
    if (!storedData) return

    const data = JSON.parse(storedData)
    const currentStep = this.stepTargets[this.currentStepValue]
    const form = currentStep.querySelector('form')
    if (!form) return

    Object.entries(data).forEach(([key, value]) => {
      const field = form.querySelector(`[name="course[${key}]"]`)
      if (field) {
        field.value = value
        if (field.tagName === 'SELECT') {
          field.dispatchEvent(new Event('change'))
        }
      }
    })
  }

  clearStoredData() {
    for (let i = 1; i <= this.totalStepsValue; i++) {
      sessionStorage.removeItem(`wizard_step_${i}`)
    }
  }

  validateCurrentStep() {
    if (this.currentStepValue === 0) return true

    const currentStep = this.stepTargets[this.currentStepValue]
    const form = currentStep.querySelector('form')
    if (!form) return true

    const fields = Array.from(form.elements)
    const isValid = fields.every(field => field.checkValidity())

    if (isValid) {
      this.storeCurrentStepData()
      return true
    }

    form.reportValidity()
    return false
  }

  async updateStepVisibility() {
    if (this.currentStepValue === 2) {
      try {
        const response = await fetch("/wizard/step/2", {
          headers: {
            "Accept": "text/html",
            "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
          }
        })
        if (!response.ok) throw new Error("Erreur lors du chargement de l'étape")
        const html = await response.text()
        this.stepTargets[2].innerHTML = html
        
        // Charger les itinéraires après que l'étape 2 est chargée
        if (this.hasWaySelectTarget) {
          console.log("🧙‍♂️ WaySelect trouvé:", this.waySelectTarget)
          this.loadItineraries()
        }
      } catch (error) {
        console.error("❌ Erreur:", error)
      }
    }

    this.stepTargets.forEach((step, index) => {
      const isVisible = index === this.currentStepValue
      step.classList.toggle('hidden', !isVisible)

      if (isVisible && index > 0) {
        this.setupRequiredFields(step)
        if (index === this.totalStepsValue - 1) {
          this.updateRecap()
        }
      }
    })
  }

  updateButtonsVisibility() {
    if (this.currentStepValue === 0) {
      this.prevButtonTarget.classList.add('hidden')
      this.nextButtonTarget.classList.add('hidden')
      this.finishButtonTarget.classList.add('hidden')
      return
    }

    this.prevButtonTarget.classList.toggle('hidden', this.currentStepValue === 1)

    const isLastStep = this.currentStepValue === this.totalStepsValue - 1
    this.nextButtonTarget.classList.toggle('hidden', isLastStep)
    this.finishButtonTarget.classList.toggle('hidden', !isLastStep)
  }

  updateProgressBar() {
    if (!this.showProgressValue || !this.hasProgressTarget) return

    const currentStep = this.currentStepValue
    const totalSteps = this.totalStepsValue - 1
    const percentage = Math.round((currentStep / totalSteps) * 100)

    // Si les éléments n'existent pas encore, on les crée
    if (!this.progressElements) {
      this.progressTarget.innerHTML = ''
      this.createProgressBarElements()
    }

    // Mise à jour des textes et de la barre de progression
    this.progressElements.stepText.textContent = `Étape ${currentStep}/${totalSteps}`
    this.progressElements.percentageText.textContent = `${percentage}%`
    this.progressElements.progressBar.style.width = `${percentage}%`
    this.progressElements.progressBar.setAttribute('aria-valuenow', percentage)
  }

  createProgressBarElements() {
    const container = document.createElement('div')
    container.className = 'mb-8'

    const textContainer = document.createElement('div')
    textContainer.className = 'flex justify-between mb-1'

    const stepText = document.createElement('span')
    stepText.className = 'text-base font-medium text-blue-700'

    const percentageText = document.createElement('span')
    percentageText.className = 'text-sm font-medium text-blue-700'

    const progressContainer = document.createElement('div')
    progressContainer.className = 'w-full bg-yellow-200 rounded-full h-2.5'

    const progressBar = document.createElement('div')
    progressBar.className = 'bg-blue h-2.5 rounded-full transition-all duration-500 ease-in-out'
    progressBar.setAttribute('role', 'progressbar')
    progressBar.setAttribute('aria-valuemin', '0')
    progressBar.setAttribute('aria-valuemax', '100')
    progressBar.setAttribute('aria-label', 'Progression du formulaire')

    // Assembler les éléments
    textContainer.appendChild(stepText)
    textContainer.appendChild(percentageText)
    progressContainer.appendChild(progressBar)
    container.appendChild(textContainer)
    container.appendChild(progressContainer)

    this.progressTarget.appendChild(container)

    // Stocker les références pour les mises à jour futures
    this.progressElements = {
      stepText,
      percentageText,
      progressBar
    }
  }

  updateRecap() {
    if (!this.hasRecapTarget) return

    const recap = this.recapTarget
    const startingDate = recap.querySelector('[data-starting-date]')
    const stayNightBefore = recap.querySelector('[data-stay-night-before]')
    const kmByDay = recap.querySelector('[data-km-by-day]')
    const averageSpeed = recap.querySelector('[data-average-speed]')
    const wayName = recap.querySelector('[data-way-name]')

    const data = {}
    for (let i = 1; i <= this.totalStepsValue - 1; i++) {
      const storedData = sessionStorage.getItem(`wizard_step_${i}`)
      if (storedData) {
        Object.assign(data, JSON.parse(storedData))
      }
    }

    if (wayName && data.way_id) {
      const waySelect = document.querySelector(`select[name="course[way_id]"]`)
      if (waySelect) {
        const selectedOption = waySelect.querySelector(`option[value="${data.way_id}"]`)
        if (selectedOption) {
          wayName.textContent = selectedOption.textContent
        }
      }
    }

    const date = new Date(data.starting_date_course)
    const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
    const formattedDate = date.toLocaleDateString('fr-FR', dateOptions)
    const previousDate = new Date(date)
    previousDate.setDate(date.getDate() - 1)
    const formattedPreviousDate = previousDate.toLocaleDateString('fr-FR', dateOptions)

    if (startingDate) {
      startingDate.textContent = formattedDate
    }

    if (stayNightBefore) {
      const template = data.stay_night_before === '1'
          ? stayNightBefore.dataset.stayTemplate
          : stayNightBefore.dataset.noStayTemplate

      stayNightBefore.textContent = template.replace('%{date}', formattedPreviousDate)
    }

    if (kmByDay) {
      const km = data.km_by_day
      if (km && km !== '0') {
        const kmContainer = kmByDay.closest('li')
        if (kmContainer) {
          kmContainer.classList.remove('hidden')
          kmByDay.textContent = km
        }
      } else {
        const kmContainer = kmByDay.closest('li')
        if (kmContainer) {
          kmContainer.classList.add('hidden')
        }
      }
    }

    if (averageSpeed) {
      const speed = data.average_speed
      if (speed && speed !== '0') {
        averageSpeed.textContent = `${speed} km/h`
      }
    }

    const form = recap.closest('form')
    if (form) {
      Object.entries(data).forEach(([key, value]) => {
        const input = form.querySelector(`input[name="course[${key}]"]`)
        if (input) {
          input.value = value
        }
      })
    }
  }

  async finish() {
    if (this.validateCurrentStep()) {
      // Désactiver le bouton de soumission
      this.finishButtonTarget.disabled = true
      this.finishButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      
      const allData = new FormData()

      if (this.poiStartIdValue) {
        allData.append('course[poi_start_id]', this.poiStartIdValue)
      }
      if (this.wayIdValue) {
        allData.append('course[way_id]', this.wayIdValue)
      }

      for (let i = 1; i <= this.totalStepsValue - 1; i++) {
        const storedData = sessionStorage.getItem(`wizard_step_${i}`)
        if (storedData) {
          const data = JSON.parse(storedData)
          Object.entries(data).forEach(([key, value]) => {
            allData.append(`course[${key}]`, value)
          })
        }
      }

      try {
        const response = await fetch('/wizard', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Accept': 'application/json'
          },
          body: allData
        })

        const result = await response.json()

        if (result.success) {
          this.clearStoredData()

          window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
              this.clearStoredData()
            }
          })

          window.location.href = result.redirect_to
        } else {
          this.showErrors(result.errors)
          // Réactiver le bouton en cas d'erreur
          this.finishButtonTarget.disabled = false
          this.finishButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        }
      } catch (error) {
        console.error('❌ Erreur lors de la création de la course:', error)
        // Réactiver le bouton en cas d'erreur
        this.finishButtonTarget.disabled = false
        this.finishButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      }
    }
  }

  showErrors(errors) {
    console.error("❌ Form errors:", errors)
    const errorDiv = this.errorsTarget
    if (Array.isArray(errors)) {
      const errorList = errors.map(error => `<li>${error}</li>`).join('')
      errorDiv.innerHTML = `<ul class="list-disc pl-5">${errorList}</ul>`
    } else {
      errorDiv.textContent = errors
    }
    errorDiv.classList.remove('hidden')
  }

  setupRequiredFields(targetStep = null) {
    const steps = targetStep ? [targetStep] : this.stepTargets

    steps.forEach(step => {
      const labels = step.querySelectorAll('label')
      labels.forEach(label => {
        if (label.querySelector('.text-red-500')) {
          const inputId = label.getAttribute('for')
          const input = step.querySelector(`#${inputId}`)
          if (input) {
            input.setAttribute('required', 'required')
          }
        }
      })
    })
  }

  validateField(event) {
    const input = event.target
    const errorDiv = this.errorsTarget

    // Réinitialiser les styles
    input.classList.remove('border-red-500', 'border-green-500')
    input.classList.add('border-gray-300')

    switch(input.name) {
      case "course[name]":
        if (input.value.length < 3) {
          input.classList.remove('border-gray-300')
          input.classList.add('border-red-500')
          this.showFieldError(errorDiv, "Le titre doit contenir au moins 3 caractères")
          return false
        } else {
          input.classList.remove('border-gray-300')
          input.classList.add('border-green-500')
        }
        break
      case "course[number_of_people]":
        const value = parseInt(input.value)
        if (value < 1) {
          input.value = 1
          input.classList.add('border-green-500')
          return true
        } else {
          input.classList.add('border-green-500')
        }
        break
    }

    this.hideFieldError(errorDiv)
    return true
  }

  showFieldError(errorDiv, message) {
    errorDiv.textContent = message
    errorDiv.classList.remove('hidden')
    errorDiv.classList.add('bg-red-50', 'p-4', 'rounded-md')
  }

  hideFieldError(errorDiv) {
    errorDiv.textContent = ''
    errorDiv.classList.add('hidden')
    errorDiv.classList.remove('bg-red-50', 'p-4', 'rounded-md')
  }

  updateKmHint(event) {
    const input = event.target
    const hint = input.parentElement.querySelector('.hint')
    if (hint && input.value) {
      hint.textContent = `Tu te projetes sur des étapes de ${input.value} kilomètres par jour`
    }
  }

  clearInput(event) {
    console.log("🧙‍♂️ Nettoyage du champ de saisie")
    const input = event.target
    input.value = ""
    input.placeholder = "Tape les premières lettres de la ville..."
    const form = input.closest('form')
    const poiStartSelect = form.querySelector('[name="course[poi_start_id]"]')
    const poiEndSelect = form.querySelector('[name="course[poi_end_id]"]')
    poiStartSelect.value = ""
    poiEndSelect.value = ""
    this.clearSuggestions()
  }

  async fetchPois(event) {
    const wayId = event.target.value
    console.log("🧙‍♂️ fetchPois - Way ID sélectionné:", wayId)
    
    // Réinitialiser les champs
    const form = event.target.closest('form')
    const poiInput = form.querySelector('[data-wizard-target="poiInput"]')
    const poiStartSelect = form.querySelector('[name="course[poi_start_id]"]')
    const poiEndSelect = form.querySelector('[name="course[poi_end_id]"]')
    
    poiInput.value = ""
    poiStartSelect.value = ""
    poiEndSelect.value = ""
    
    // Si une voie est sélectionnée, initialiser avec la ville de départ par défaut
    if (wayId) {
      const selectedOption = event.target.selectedOptions[0]
      const defaultCityId = selectedOption.dataset.cityStartId
      const defaultCityName = selectedOption.dataset.cityStartName
      
      if (defaultCityId && defaultCityName) {
        poiInput.value = defaultCityName
        poiStartSelect.value = defaultCityId
        poiEndSelect.value = defaultCityId
      }
      
      // Précharger toutes les villes de la voie
      try {
        const response = await fetch(`/api/pois/cities?way_id=${wayId}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        if (!response.ok) throw new Error('Erreur lors du chargement des villes')
        
        const cities = await response.json()
        // Stocker dans le cache avec une clé simple par voie
        this.poiCacheValue[wayId] = cities
      } catch (error) {
        console.error('❌ Erreur:', error)
      }
      
      // Activer l'input pour la recherche de POIs
      poiInput.disabled = false
      poiInput.placeholder = "Tape les premières lettres de la ville..."
    } else {
      // Désactiver l'input si aucune voie n'est sélectionnée
      poiInput.disabled = true
      poiInput.placeholder = "Sélectionne d'abord une voie..."
    }
  }

  updatePoiSuggestions(pois, input) {
    console.log("🧙‍♂️ Mise à jour des suggestions:", { 
      nombrePois: pois.length,
      inputValue: input.value
    })

    this.clearSuggestions()
    
    // Filtrer les POIs pour ne garder que ceux qui commencent par la recherche
    const query = input.value.toLowerCase()
    const filteredPois = pois.filter(poi => 
      poi.name.toLowerCase().startsWith(query)
    )

    if (filteredPois.length === 0) {
      return
    }
    
    const form = input.closest('form')
    const poiStartSelect = form.querySelector('[name="course[poi_start_id]"]')
    const poiEndSelect = form.querySelector('[name="course[poi_end_id]"]')
    
    if (!poiStartSelect || !poiEndSelect) {
      console.error("❌ Champs de sélection POI non trouvés")
      return
    }

    const suggestionsList = document.createElement('ul')
    suggestionsList.className = 'poi-suggestions absolute z-10 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm'
    
    filteredPois.forEach(poi => {
      console.log("🧙‍♂️ Ajout suggestion:", poi.name)
      const li = document.createElement('li')
      li.className = 'text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 hover:bg-blue-100'
      li.textContent = poi.name
      
      li.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        input.value = poi.name
        poiStartSelect.value = poi.id
        poiEndSelect.value = poi.id
        this.clearSuggestions()
      })
      
      suggestionsList.appendChild(li)
    })
    
    input.insertAdjacentElement('afterend', suggestionsList)
  }

  clearSuggestions() {
    console.log("🧙‍♂️ Nettoyage des suggestions")
    const suggestionsList = document.querySelector('.poi-suggestions')
    if (suggestionsList) {
      suggestionsList.remove()
    }
  }

  cleanCache() {
    const cacheEntries = Object.entries(this.poiCacheValue)
    if (cacheEntries.length > this.constructor.POI_CACHE_SIZE) {
      console.log("🧙‍♂️ Nettoyage du cache - Avant:", cacheEntries.length, "entrées")
      this.poiCacheValue = Object.fromEntries(
        cacheEntries.slice(-this.constructor.POI_CACHE_SIZE)
      )
      console.log("🧙‍♂️ Nettoyage du cache - Après:", Object.keys(this.poiCacheValue).length, "entrées")
    }
  }

  async searchPois(event) {
    const query = event.target.value.toLowerCase()
    console.log("🧙‍♂️ searchPois - Démarrage de la recherche:", query)

    if (!this.hasWaySelectTarget) {
      console.log("❌ WaySelect non trouvé")
      return
    }

    const waySelect = this.waySelectTarget
    const wayId = waySelect.value

    if (!wayId) {
      console.log("❌ Aucun itinéraire sélectionné")
      return
    }

    if (query.length < this.constructor.MINIMUM_SEARCH_LENGTH) {
      console.log("❌ Query trop courte")
      this.clearSuggestions()
      return
    }

    // Vérifier si nous avons les villes en cache
    if (this.poiCacheValue[wayId]) {
      console.log("🧙‍♂️ Filtrage local des villes")
      const cities = this.poiCacheValue[wayId]
      const filteredCities = cities.filter(city => 
        city.name.toLowerCase().includes(query)
      ).slice(0, 50)
      
      this.updatePoiSuggestions(filteredCities, event.target)
      return
    }

    // Si pas de cache, charger les villes filtrées depuis l'API
    try {
      const response = await fetch(`/api/pois/cities?way_id=${wayId}&q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Erreur lors du chargement des villes')
      }

      const cities = await response.json()
      console.log("🧙‍♂️ searchPois - Villes reçues:", cities)
      
      if (cities.length === 0) {
        this.clearSuggestions()
        return
      }

      this.updatePoiSuggestions(cities, event.target)
    } catch (error) {
      console.error('❌ Erreur:', error.message)
      this.clearSuggestions()
    }
  }

  showError(message) {
    const errorDiv = this.errorsTarget
    errorDiv.textContent = message
    errorDiv.classList.remove('hidden')
    errorDiv.classList.add('bg-red-50', 'text-red-700', 'p-4', 'rounded-md', 'mb-4')

    // Masquer l'erreur après 5 secondes
    setTimeout(() => {
      this.hideError()
    }, 5000)
  }

  hideError() {
    const errorDiv = this.errorsTarget
    errorDiv.classList.add('hidden')
    errorDiv.classList.remove('bg-red-50', 'text-red-700', 'p-4', 'rounded-md', 'mb-4')
    errorDiv.textContent = ''
  }

  // Ajouter la fonction debounce
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func.apply(this, args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}